import React, { useEffect, useState } from "react";
import { CalendarClock, AlertCircle } from "lucide-react";
import { api } from "../lib/api";

const CAMP_SHORT = {
  "El Aaiún (Laâyoune)": "El Aaiún",
  "Ausserd (Aousserd)": "Ausserd",
};

function shortCamp(c) {
  return CAMP_SHORT[c] || c;
}

function formatEsDate(iso) {
  try {
    return new Intl.DateTimeFormat("es-ES", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function countdownLabel(days, hours) {
  if (days === 0) return "¡Sale hoy!";
  if (days < 7) return `Faltan ${days} días y ${hours} horas`;
  return `Faltan ${days} días`;
}

/**
 * Soft-warning banner: shows the nearest active future MercaGo departure so
 * senders know the deadline. Persistent (not dismissible), safe to mount on
 * any page. Renders nothing when there's no future departure.
 */
export default function NextDepartureBanner() {
  const [dep, setDep] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () =>
      api
        .get("/departures/next")
        .then((r) => alive && setDep(r.data?.next === null ? null : r.data))
        .catch(() => alive && setDep(null));
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      alive = false;
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  if (!dep || !dep.id) return null;

  const sameDay = dep.days_remaining === 0;
  const pill = countdownLabel(dep.days_remaining, dep.hours_remaining);
  const dateStr = formatEsDate(dep.date);
  const campSuffix = dep.destination_camp
    ? ` · Destino: ${shortCamp(dep.destination_camp)}`
    : "";

  const wrapperCls = sameDay
    ? "border-l-4 border-amber-500 bg-amber-50 text-amber-900"
    : "border-l-4 border-[#00953B] bg-emerald-50 text-emerald-900";

  const Icon = sameDay ? AlertCircle : CalendarClock;

  return (
    <div
      role="status"
      data-testid="wizard-next-departure-banner"
      className={`rounded-xl ${wrapperCls} px-4 py-3 mb-6 flex items-start gap-3 shadow-sm`}
    >
      <Icon aria-hidden className="h-5 w-5 mt-0.5 flex-shrink-0" />
      <div className="flex-1 text-sm leading-relaxed">
        <div
          data-testid="wizard-next-departure-date"
          className="font-bold capitalize"
        >
          Próxima salida — {dateStr}
        </div>
        <div className="mt-0.5">
          <span
            data-testid="wizard-next-departure-countdown"
            className={`inline-flex items-center rounded-full text-xs font-semibold px-2 py-0.5 mr-2 ${
              sameDay
                ? "bg-amber-500 text-white"
                : "bg-white text-[#00753a] border border-emerald-200"
            }`}
          >
            {pill}
          </span>
          {sameDay
            ? "Envía tu paquete cuanto antes."
            : "Envía tu paquete antes de esta fecha para que llegue en esta salida."}
          {campSuffix && (
            <span className="text-xs opacity-75 ml-1">{campSuffix}</span>
          )}
        </div>
      </div>
    </div>
  );
}
